import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Calendar, Users, Dog, MapPin, Home } from "lucide-react";
import { api } from "@/lib/api";

const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function SearchBar({ compact = false, initial = {} }) {
  const navigate = useNavigate();
  const [region, setRegion] = useState(initial.region || "");
  const [postcode, setPostcode] = useState(initial.postcode || "");
  const [arrival, setArrival] = useState(initial.arrival || todayPlus(21));
  const [departure, setDeparture] = useState(initial.departure || todayPlus(24));
  const [adults, setAdults] = useState(initial.adults ? Number(initial.adults) : 2);
  const [children, setChildren] = useState(initial.children ? Number(initial.children) : 0);
  const [pets, setPets] = useState(initial.pets === "true" || initial.pets === true);
  const [regions, setRegions] = useState([]);

  useEffect(() => { api.regions().then(setRegions); }, []);

  const submit = (e) => {
    e?.preventDefault?.();
    const params = new URLSearchParams();
    if (region) params.set("region", region);
    if (postcode.trim()) params.set("postcode", postcode.trim().toUpperCase());
    params.set("arrival", arrival);
    params.set("departure", departure);
    params.set("adults", String(adults));
    params.set("children", String(children));
    if (pets) params.set("pets", "true");
    navigate(`/search?${params.toString()}`);
  };

  return (
    <form
      onSubmit={submit}
      className={`${compact ? "bg-white" : "glass"} rounded-2xl shadow-xl p-2 sm:p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-stretch`}
      data-testid="search-bar"
    >
      <div className="md:col-span-3 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/90 border border-border">
        <MapPin className="w-4 h-4 text-forest shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow text-[10px]">Region</div>
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="w-full bg-transparent outline-none text-sm font-medium cursor-pointer"
            data-testid="search-input-region"
          >
            <option value="">All UK regions</option>
            {regions.map((r) => (
              <option key={r.slug} value={r.name}>{r.name} ({r.count})</option>
            ))}
          </select>
        </div>
      </div>
      <div className="md:col-span-2 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/90 border border-border">
        <Home className="w-4 h-4 text-forest shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow text-[10px]">Your postcode</div>
          <input
            type="text"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            placeholder="e.g. SW1A 1AA"
            maxLength={8}
            className="w-full bg-transparent outline-none text-sm font-medium placeholder:text-ink/40 uppercase"
            data-testid="search-input-postcode"
          />
        </div>
      </div>
      <div className="md:col-span-2 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/90 border border-border">
        <Calendar className="w-4 h-4 text-forest shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow text-[10px]">Arrive</div>
          <input
            type="date"
            value={arrival}
            onChange={(e) => setArrival(e.target.value)}
            className="w-full bg-transparent outline-none text-sm font-medium"
            data-testid="search-input-arrival"
          />
        </div>
      </div>
      <div className="md:col-span-2 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/90 border border-border">
        <Calendar className="w-4 h-4 text-forest shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow text-[10px]">Depart</div>
          <input
            type="date"
            value={departure}
            onChange={(e) => setDeparture(e.target.value)}
            className="w-full bg-transparent outline-none text-sm font-medium"
            data-testid="search-input-departure"
          />
        </div>
      </div>
      <div className="md:col-span-2 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/90 border border-border">
        <Users className="w-4 h-4 text-forest shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow text-[10px]">Guests</div>
          <div className="flex gap-2 text-sm font-medium">
            <select value={adults} onChange={(e) => setAdults(Number(e.target.value))} className="bg-transparent outline-none cursor-pointer" data-testid="search-input-adults">
              {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n} adult{n>1?"s":""}</option>)}
            </select>
            <select value={children} onChange={(e) => setChildren(Number(e.target.value))} className="bg-transparent outline-none cursor-pointer" data-testid="search-input-children">
              {[0,1,2,3,4,5,6].map(n => <option key={n} value={n}>{n} child{n!==1?"ren":""}</option>)}
            </select>
          </div>
        </div>
      </div>
      <div className="md:col-span-1 flex items-stretch gap-2">
        <label className="flex-1 flex items-center gap-1.5 px-2 py-2 rounded-xl bg-white/90 border border-border cursor-pointer" data-testid="search-input-pets-label">
          <Dog className="w-4 h-4 text-forest" />
          <input type="checkbox" checked={pets} onChange={(e) => setPets(e.target.checked)} className="ml-auto accent-forest" data-testid="search-input-pets" />
        </label>
        <button className="btn-primary px-4 shrink-0" data-testid="search-submit-button" type="submit">
          <Search className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}
